/**
 * WebAuthn browser helpers for passkey registration and authentication.
 *
 * Uses the raw WebAuthn browser API (navigator.credentials) directly,
 * avoiding the @simplewebauthn/browser dependency. The server's registration
 * and authentication options map directly to CredentialCreationOptions /
 * CredentialRequestOptions after base64url decoding.
 */

// ---------------------------------------------------------------------------
// Base64url encoding/decoding utilities
// ---------------------------------------------------------------------------

/**
 * Decode a base64url string to an ArrayBuffer.
 * Handles the URL-safe alphabet and missing padding.
 */
export function base64urlToBuffer(base64url: string): ArrayBuffer {
  // Convert base64url to standard base64
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  // Add padding if needed
  const pad = base64.length % 4;
  if (pad === 2) base64 += '==';
  else if (pad === 3) base64 += '=';

  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Encode an ArrayBuffer to a base64url string.
 * Produces the URL-safe alphabet without padding.
 */
export function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// ---------------------------------------------------------------------------
// Feature detection
// ---------------------------------------------------------------------------

/**
 * Check if the browser supports WebAuthn / passkeys.
 * Returns false on the server (SSR) and in unsupported browsers.
 */
export function isWebAuthnSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof PublicKeyCredential !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.credentials !== 'undefined'
  );
}

// ---------------------------------------------------------------------------
// Server option types (match the DTOs from PasskeyService)
// ---------------------------------------------------------------------------

export interface ServerRegistrationOptions {
  challengeId: string;
  challenge: string;
  rp: { name: string; id: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: Array<{ type: 'public-key'; alg: number }>;
  timeout: number;
  excludeCredentials: Array<{
    id: string;
    type: 'public-key';
    transports?: string[];
  }>;
  authenticatorSelection: {
    residentKey: string;
    userVerification: string;
  };
  attestation: string;
}

export interface ServerAuthenticationOptions {
  challengeId: string;
  challenge: string;
  rpId: string;
  timeout: number;
  allowCredentials: Array<{
    id: string;
    type: 'public-key';
    transports?: string[];
  }>;
  userVerification: string;
}

// ---------------------------------------------------------------------------
// Registration response
// ---------------------------------------------------------------------------

export interface RegistrationResult {
  challengeId: string;
  credentialId: string;
  publicKey: string; // base64url
  counter: number;
  deviceType?: string;
  backedUp?: boolean;
  transports?: string[];
}

/**
 * Start a WebAuthn registration ceremony.
 *
 * Converts the server's registration options to the format expected by
 * navigator.credentials.create() and returns the attestation response
 * in a format ready to send back to the server.
 */
export async function startRegistration(
  options: ServerRegistrationOptions
): Promise<RegistrationResult> {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported in this browser');
  }

  const publicKeyOptions: PublicKeyCredentialCreationOptions = {
    challenge: base64urlToBuffer(options.challenge),
    rp: options.rp,
    user: {
      id: new TextEncoder().encode(options.user.id),
      name: options.user.name,
      displayName: options.user.displayName,
    },
    pubKeyCredParams: options.pubKeyCredParams.map((p) => ({
      type: p.type,
      alg: p.alg,
    })),
    timeout: options.timeout,
    excludeCredentials: options.excludeCredentials.map((c) => ({
      id: base64urlToBuffer(c.id),
      type: c.type,
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
    authenticatorSelection: {
      residentKey: options.authenticatorSelection.residentKey as ResidentKeyRequirement,
      userVerification: options.authenticatorSelection.userVerification as UserVerificationRequirement,
    },
    attestation: options.attestation as AttestationConveyancePreference,
  };

  const credential = (await navigator.credentials.create({
    publicKey: publicKeyOptions,
  })) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error('Credential creation was cancelled or failed');
  }

  const response = credential.response as AuthenticatorAttestationResponse;

  // Extract authenticator data for device type and backed-up status
  const authData = new Uint8Array(response.getAuthenticatorData());
  const flags = authData[32]; // Flags byte is at offset 32
  const backedUp = (flags & 0x10) !== 0; // BS flag (bit 4)

  // Get public key in SPKI format
  const publicKeySpki = response.getPublicKey();
  if (!publicKeySpki) {
    throw new Error('Failed to extract public key from attestation response');
  }

  // Extract transports if available
  let transports: string[] | undefined;
  if (typeof response.getTransports === 'function') {
    transports = response.getTransports();
  }

  return {
    challengeId: options.challengeId,
    credentialId: bufferToBase64url(credential.rawId),
    publicKey: bufferToBase64url(publicKeySpki),
    counter: 0, // Initial counter is 0 for new credentials
    deviceType: credential.authenticatorAttachment === 'platform' ? 'platform' : 'cross-platform',
    backedUp,
    transports,
  };
}

// ---------------------------------------------------------------------------
// Authentication response
// ---------------------------------------------------------------------------

export interface AuthenticationResult {
  challengeId: string;
  credentialId: string;
  authenticatorData: string; // base64url
  signature: string; // base64url
  counter: number;
}

/**
 * Start a WebAuthn authentication ceremony.
 *
 * Converts the server's authentication options to the format expected by
 * navigator.credentials.get() and returns the assertion response
 * in a format ready to send back to the server.
 */
export async function startAuthentication(
  options: ServerAuthenticationOptions
): Promise<AuthenticationResult> {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported in this browser');
  }

  const publicKeyOptions: PublicKeyCredentialRequestOptions = {
    challenge: base64urlToBuffer(options.challenge),
    rpId: options.rpId,
    timeout: options.timeout,
    allowCredentials: options.allowCredentials.map((c) => ({
      id: base64urlToBuffer(c.id),
      type: c.type,
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
    userVerification: options.userVerification as UserVerificationRequirement,
  };

  const credential = (await navigator.credentials.get({
    publicKey: publicKeyOptions,
  })) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error('Authentication was cancelled or failed');
  }

  const response = credential.response as AuthenticatorAssertionResponse;

  // Parse authenticator data to extract counter (bytes 33-36, big-endian uint32)
  const authDataArray = new Uint8Array(response.authenticatorData);
  const counterView = new DataView(authDataArray.buffer, authDataArray.byteOffset + 33, 4);
  const counter = counterView.getUint32(0, false); // big-endian

  return {
    challengeId: options.challengeId,
    credentialId: bufferToBase64url(credential.rawId),
    authenticatorData: bufferToBase64url(response.authenticatorData),
    signature: bufferToBase64url(response.signature),
    counter,
  };
}
